import { profileAddress } from "@/libs/ApiFetchMeme";
import { Profile } from "@/types/meme/tokens";
import { useEffect, useMemo, useState } from "react";
import { createTrackedSelector } from "react-tracked";
import { create } from "zustand";
import { useEditProfile } from "./useEditProfile";
import { useAccount } from "../useAccount";

interface ProfileMap {
  [address: string]: Profile | undefined;
}

interface ProfileContextInterface {
  profiles: ProfileMap;
  addProfile: (profile: Profile | undefined) => void;
}

export const ProfileContext = create<ProfileContextInterface>((set, get) => ({
  profiles: {},

  addProfile: (profile: Profile | undefined) => {
    if (!profile) {
      return;
    }

    const t = { ...get().profiles };
    t[profile?.user_address] = profile;
    set({ profiles: t });
  },
}));

const useProfileContextStore = createTrackedSelector(ProfileContext);

export const useProfile = (address: string | undefined) => {
  // const [profile, setProfile] = useState<Profile | undefined>(undefined);
  const { address: ownAddress } = useAccount();
  const { profiles, addProfile } = useProfileContextStore();
  const [isFirstFetch, setIsFirstFetch] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { isTeam, setIsTeam } = useEditProfile();

  useEffect(() => {
    const fetch = async () => {
      if (!address) {
        return;
      }
      if (profiles[address]) {
        setIsLoading(false);
        setIsFirstFetch(true);
        return;
      }

      setIsLoading(true);
      const data = await profileAddress(address);

      if (data.data) {
        addProfile(data.data);
        if (address === ownAddress) {
          setIsTeam(!!data.data.team);
        }
      }

      setIsLoading(false);
      setIsFirstFetch(true);
    };

    fetch();
  }, [address, profiles, addProfile, setIsTeam, ownAddress]);

  const profile = useMemo(
    () => (address ? profiles[address] : undefined),
    [address, profiles]
  );

  return {
    profile,
    isFirstFetch,
    setIsFirstFetch,
    isLoading,
    setIsLoading,
  };
};
